/**
 * Pinia Data Store for Cirrus Reader Data
 * Contains a store for anything related to the reading experience including current page, renderer etc
 */
import { defineStore } from 'pinia'
import type { 
  ICirrusReaderContentPositionData, 
  ICirrusReaderNavigationCollection, 
  ICirrusReaderNavigationTreeData, 
  ICirrusReaderPublicationData, 
  ICirrusReaderSyntheticPageData, 
  ICirrusReaderSyntheticPageListData, 
  ICirrusReaderVisibleRangeData 
} from '../shared/CirrusReaderModel';
import storage from '../utils/Storage';


interface CirrusReaderNavigationState {
  readableCurrentPage: string,
  readableTotalPages: string,
  pageNumber: number,
  totalPageNumber: number,
  sliderStepSize: number,
  currentProgress: number,
  pageList?: ICirrusReaderSyntheticPageListData,
  currentLocator?: string,
  readingPositionData?: ICirrusReaderContentPositionData,
  navigationTree?:  ICirrusReaderNavigationCollection,
  publicationData?: ICirrusReaderPublicationData,
  visibleRange?: IntRange,
}


export const useCirrusReaderNavigation = defineStore('cirrusReaderNavigation', {
  state: (): CirrusReaderNavigationState => {
    return {  
      readableCurrentPage: '0',
      readableTotalPages: '1',
      pageNumber: 0,
      totalPageNumber: 1,
      sliderStepSize: 1,
      currentProgress: 0,
      pageList: undefined,
      currentLocator: undefined,
      readingPositionData: undefined,
      navigationTree: undefined,
      publicationData: undefined,
      visibleRange: undefined,
    }
  },
  actions: {
    calculateProgress() {
      this.currentProgress = Math.round((this.pageNumber / this.totalPageNumber) * 100);
    },
    setReadablePage(page?: string) {
      if (page) {
        this.readableCurrentPage = page;
      } else {
        const currentSyntheticPage = this.getSyntheticPageDataFromPosition(this.pageNumber);
        this.readableCurrentPage = currentSyntheticPage?.label ?? '';
      }
      this.calculateProgress();
    },
    setReadableTotalPages(totalPages?: string) {
      if (totalPages) {
        this.readableTotalPages = totalPages;
      } else {
        const syntheticPages = useCirrusPageListStore().pageList;
        this.readableTotalPages = syntheticPages.findLast(()=>true)?.label ?? '';
      }
    },
    getReadablePage(): string {
      return this.readableCurrentPage;
    },
    getReadableTotalPages(): string{
      return this.readableTotalPages;
    },
    getCurrentPage(){
      return this.pageNumber;
    },
    getTotalPages(){
      return this.totalPageNumber;
    },
    setReadingPositionData(data: ICirrusReaderContentPositionData){
      this.currentLocator = data.locator || "";
      this.readingPositionData = data;
    },
    setNavigationTree(navigationTreeData: ICirrusReaderNavigationTreeData) {
      if (typeof navigationTreeData.collections !== 'undefined'){
        const toc = navigationTreeData.collections.find(function(item) {
          return item.type === "TOC";
        });
        if (toc){
          this.navigationTree = toc;
        }
      }
    },
    setPublicationData(publicationData: ICirrusReaderPublicationData){
      this.publicationData = publicationData;
    },
    getSyntheticPageDataFromPosition(position: number): ICirrusReaderSyntheticPageData | undefined {
        return useCirrusPageListStore().pageList.find((page)=>{
            return (position >= page.range.start && position < page.range.end) || (position === page.range.start && position === page.range.end);
        });
    },
    setSliderStepSize(value: number): void {
      this.sliderStepSize = value;
    },
    setSliderProgressionValue(value: number): void {
      const timelineData = useCirrusPageListStore().timelineData;
      if (timelineData !== undefined){
        this.pageNumber = value;
        this.totalPageNumber = timelineData.length;
        this.setReadablePage();
        this.setReadableTotalPages();
      }
    },
    setVisibleRangeData(data: ICirrusReaderVisibleRangeData) {
      if(data.timelineRange) {
        this.visibleRange = new IntRange(data.timelineRange.start, data.timelineRange.end);
      }
    },
    toJSON() {
      return {
        readableCurrentPage: this.readableCurrentPage,
        readableTotalPages: this.readableTotalPages,
        currentProgress: this.currentProgress,
        pageNumber: this.pageNumber,
        totalPageNumber: this.totalPageNumber,
        currentLocator: this.currentLocator,
        sliderStepSize: this.sliderStepSize,
      };
    },
  },
  persist: {
    pick: [
      'readableCurrentPage',
      'readableTotalPages',
      'currentProgress',
      'pageNumber',
      'totalPageNumber',
      'currentLocator',
      'sliderStepSize',
    ],
    storage
  }
});
